import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Activity,
  Users,
  Store,
  Tags,
  RefreshCw,
} from "lucide-react";
import ModernStatCard from "../components/ui/ModernStatCard";
import BarChart from "../components/ui/BarChart";
import ActivityList from "../components/ui/ActivityList";
import {
  getTransactions,
  getOutlets,
  getCategories,
  getUsers,
} from "../apinew";

export default function DashboardOverview() {
  const [stats, setStats] = useState({
    totalIncome: 0,
    totalExpense: 0,
    balance: 0,
    transactionCount: 0,
    outletsCount: 0,
    categoriesCount: 0,
    usersCount: 0,
  });

  const [trends, setTrends] = useState({
    balanceTrend: 0,
    incomeTrend: 0,
    expenseTrend: 0,
    transactionTrend: 0,
  });

  const [transactions, setTransactions] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState("7days"); // "7days", "1month", "3months", "1year"

  const rawUser = localStorage.getItem("user");
  const currentUser = rawUser ? JSON.parse(rawUser) : null;
  const isAdmin = currentUser?.role === "admin";
  const currentUserId = currentUser?.user_id || currentUser?.id;

  // Calculate percentage change
  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) {
      return current > 0 ? 100 : 0;
    }
    return ((current - previous) / previous) * 100;
  };

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);

    let filteredTrx = [];
    let monthlyTrx = [];
    let income = 0;
    let expense = 0;
    let outletsList = [];
    let categoriesList = [];
    let usersCount = 0;

    // Load transactions (with error handling)
    try {
      const trxData = await getTransactions();
      const trxList = Array.isArray(trxData) ? trxData : [];

      // Batasi transaksi sesuai role (user hanya melihat miliknya)
      filteredTrx = isAdmin
        ? trxList
        : trxList.filter((t) => {
            const ownerId =
              t.user_id || t.userId || t.user?.id || t.owner_id || t.ownerId;
            return ownerId ? String(ownerId) === String(currentUserId) : true;
          });

      console.log("Current User ID:", currentUserId);
      console.log("Total transactions from API:", trxList.length);
      console.log("Filtered transactions for this user:", filteredTrx.length);
      console.log("Is Admin:", isAdmin);

      setTransactions(filteredTrx);

      const isSameMonth = (dateValue) => {
        const d = dateValue ? new Date(dateValue) : null;
        if (!d || isNaN(d)) return false;
        const now = new Date();
        return (
          d.getMonth() === now.getMonth() &&
          d.getFullYear() === now.getFullYear()
        );
      };

      const typeIsIncome = (t) =>
        t.type === "income" ||
        t.type === "pemasukan" ||
        t.type === "pemasukkan";
      const typeIsExpense = (t) =>
        t.type === "expense" || t.type === "pengeluaran";

      monthlyTrx = filteredTrx.filter((t) =>
        isSameMonth(t.date || t.created_at)
      );

      // Hitung statistik bulan ini
      income = monthlyTrx
        .filter((t) => typeIsIncome(t))
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

      expense = monthlyTrx
        .filter((t) => typeIsExpense(t))
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

      // Hitung statistik bulan lalu untuk perbandingan
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);

      const lastMonthTrx = filteredTrx.filter((t) => {
        const d =
          t.date || t.created_at ? new Date(t.date || t.created_at) : null;
        if (!d || isNaN(d)) return false;
        return (
          d.getMonth() === lastMonth.getMonth() &&
          d.getFullYear() === lastMonth.getFullYear()
        );
      });

      const lastMonthIncome = lastMonthTrx
        .filter((t) => typeIsIncome(t))
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

      const lastMonthExpense = lastMonthTrx
        .filter((t) => typeIsExpense(t))
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

      const lastMonthBalance = lastMonthIncome - lastMonthExpense;
      const currentBalance = income - expense;

      // Hitung persentase perubahan
      const balanceTrend = calculatePercentageChange(
        currentBalance,
        lastMonthBalance
      );
      const incomeTrend = calculatePercentageChange(income, lastMonthIncome);
      const expenseTrend = calculatePercentageChange(expense, lastMonthExpense);
      const transactionTrend = calculatePercentageChange(
        monthlyTrx.length,
        lastMonthTrx.length
      );

      setTrends({
        balanceTrend,
        incomeTrend,
        expenseTrend,
        transactionTrend,
      });
    } catch (error) {
      console.error("❌ Error loading transactions:", error);
      // Continue execution - don't break the whole dashboard
    }

    // Load outlets and categories (with separate error handling)
    try {
      console.log("📡 Fetching outlets and categories...");
      const [outlets, categories] = await Promise.all([
        getOutlets(),
        getCategories(),
      ]);

      console.log("🔍 Raw outlets response:", outlets);
      console.log("🔍 Raw categories response:", categories);

      const outletsListRaw = Array.isArray(outlets) ? outlets : [];
      const categoriesListRaw = Array.isArray(categories) ? categories : [];

      // Outlet dan kategori TIDAK difilter per-user
      // Semua user bisa melihat semua outlet dan kategori
      outletsList = outletsListRaw;
      categoriesList = categoriesListRaw;

      console.log("📊 Outlets from API:", outletsListRaw.length);
      console.log("📊 Outlets for dashboard:", outletsList.length);
      console.log("🏪 Sample outlet:", outletsListRaw[0]);
      console.log("📊 Categories from API:", categoriesListRaw.length);
      console.log("📊 Categories for dashboard:", categoriesList.length);
      console.log("🏷️ Sample category:", categoriesListRaw[0]);
    } catch (error) {
      console.error("❌ Error loading outlets/categories:", error);
      // Continue execution - set to empty arrays
      outletsList = [];
      categoriesList = [];
    }

    // Load users count (admin only)
    if (isAdmin) {
      try {
        const users = await getUsers();
        usersCount = Array.isArray(users) ? users.length : 0;
      } catch (err) {
        console.error("❌ Error loading users:", err);
        usersCount = 0;
      }
    }

    // Generate chart data (last 7 days)
    const { data: last7Days, labels: dayLabels } =
      generateLast7DaysData(filteredTrx);
    console.log("📈 Chart data (7 days):", last7Days);
    console.log("📈 Chart data detail:", JSON.stringify(last7Days));
    console.log("📅 Chart labels:", dayLabels);

    // Update chart immediately when data loads
    setChartData(last7Days);
    setChartLabels(dayLabels);

    const newStats = {
      totalIncome: income,
      totalExpense: expense,
      balance: income - expense,
      transactionCount: monthlyTrx.length,
      outletsCount: outletsList.length,
      categoriesCount: categoriesList.length,
      usersCount,
    };

    console.log("💾 Setting stats:", newStats);
    setStats(newStats);
    setLoading(false);
  };

  const generateChartData = (trxList, period) => {
    if (period === "7days") {
      return generateLast7DaysData(trxList);
    } else if (period === "1month") {
      return generateLastMonthData(trxList);
    } else if (period === "3months") {
      return generateLast3MonthsData(trxList);
    } else if (period === "6months") {
      return generateLast6MonthsData(trxList);
    } else if (period === "1year") {
      return generateLastYearData(trxList);
    }
    return generateLast7DaysData(trxList);
  };

  const generateLast7DaysData = (trxList) => {
    const data = [];
    const labels = [];
    const dayNames = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];

    console.log(
      "🔍 Generating chart from transactions (7 days):",
      trxList.length
    );

    // Get current date and find start of this week (Monday)
    const today = new Date();
    const dayOfWeek = today.getDay();
    // Convert Sunday (0) to 6, and shift so Monday = 0
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - daysFromMonday);
    weekStart.setHours(0, 0, 0, 0);

    // Generate 7 days starting from Monday
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      const dayOfWeekNum = dayStart.getDay();
      labels.push(dayNames[dayOfWeekNum]);

      const dayTransactions = trxList.filter((t) => {
        const trxDate = new Date(t.date || t.created_at);
        return trxDate >= dayStart && trxDate <= dayEnd;
      });

      const dayIncome = dayTransactions
        .filter(
          (t) =>
            t.type === "income" ||
            t.type === "pemasukan" ||
            t.type === "pemasukkan"
        )
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

      console.log(
        `Day ${i}: ${dayStart.toLocaleDateString()} (${
          dayNames[dayOfWeekNum]
        }) = Rp ${dayIncome} (${dayTransactions.length} trx)`
      );
      data.push(dayIncome / 1000);
    }
    return { data, labels };
  };

  const generateLastMonthData = (trxList) => {
    const data = [];
    const labels = [];

    console.log(
      "🔍 Generating chart from transactions (1 month):",
      trxList.length
    );

    // Get current month
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const monthStart = new Date(year, month, 1);

    // Find Monday of the week that contains monthStart
    let weekStart = new Date(monthStart);
    const dayOfWeek = weekStart.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    weekStart.setDate(weekStart.getDate() - daysFromMonday);
    weekStart.setHours(0, 0, 0, 0);

    // Generate weeks until end of month
    const monthEnd = new Date(year, month + 1, 0);
    while (weekStart <= monthEnd) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      const weekStartDay = weekStart.getDate();
      const weekEndDay = weekEnd.getDate();
      const monthName = weekStart.toLocaleDateString("id-ID", {
        month: "short",
      });
      labels.push(`${weekStartDay}-${weekEndDay} ${monthName}`);

      const weekTransactions = trxList.filter((t) => {
        const trxDate = new Date(t.date || t.created_at);
        return trxDate >= weekStart && trxDate <= weekEnd;
      });

      const weekIncome = weekTransactions
        .filter(
          (t) =>
            t.type === "income" ||
            t.type === "pemasukan" ||
            t.type === "pemasukkan"
        )
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

      data.push(weekIncome / 1000);
      weekStart.setDate(weekStart.getDate() + 7);
    }
    return { data, labels };
  };

  const generateLast3MonthsData = (trxList) => {
    const data = [];
    const labels = [];

    console.log(
      "🔍 Generating chart from transactions (3 months):",
      trxList.length
    );

    // Get last 3 months, show monthly
    const today = new Date();
    const threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    // Start from first day of month containing threeMonthsAgo
    let monthStart = new Date(
      threeMonthsAgo.getFullYear(),
      threeMonthsAgo.getMonth(),
      1
    );
    monthStart.setHours(0, 0, 0, 0);

    // Generate months until today
    while (monthStart <= today) {
      // eslint-disable-next-line no-loop-func
      const monthEnd = new Date(
        monthStart.getFullYear(),
        monthStart.getMonth() + 1,
        0
      );
      monthEnd.setHours(23, 59, 59, 999);

      labels.push(monthStart.toLocaleDateString("id-ID", { month: "short" }));

      // eslint-disable-next-line no-loop-func
      const monthTransactions = trxList.filter((t) => {
        const trxDate = new Date(t.date || t.created_at);
        return trxDate >= monthStart && trxDate <= monthEnd;
      });

      const monthIncome = monthTransactions
        .filter(
          (t) =>
            t.type === "income" ||
            t.type === "pemasukan" ||
            t.type === "pemasukkan"
        )
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

      data.push(monthIncome / 1000);
      monthStart = new Date(
        monthStart.getFullYear(),
        monthStart.getMonth() + 1,
        1
      );
    }

    return { data, labels };
  };

  const generateLast6MonthsData = (trxList) => {
    const data = [];
    const labels = [];

    console.log(
      "🔍 Generating chart from transactions (6 months):",
      trxList.length
    );

    // Get last 6 months, show monthly
    const today = new Date();
    const sixMonthsAgo = new Date(today);
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    // Start from first day of month containing sixMonthsAgo
    let monthStart = new Date(
      sixMonthsAgo.getFullYear(),
      sixMonthsAgo.getMonth(),
      1
    );
    monthStart.setHours(0, 0, 0, 0);

    // Generate months until today
    while (monthStart <= today) {
      // eslint-disable-next-line no-loop-func
      const monthEnd = new Date(
        monthStart.getFullYear(),
        monthStart.getMonth() + 1,
        0
      );
      monthEnd.setHours(23, 59, 59, 999);

      labels.push(monthStart.toLocaleDateString("id-ID", { month: "short" }));

      // eslint-disable-next-line no-loop-func
      const monthTransactions = trxList.filter((t) => {
        const trxDate = new Date(t.date || t.created_at);
        return trxDate >= monthStart && trxDate <= monthEnd;
      });

      const monthIncome = monthTransactions
        .filter(
          (t) =>
            t.type === "income" ||
            t.type === "pemasukan" ||
            t.type === "pemasukkan"
        )
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

      data.push(monthIncome / 1000);
      monthStart = new Date(
        monthStart.getFullYear(),
        monthStart.getMonth() + 1,
        1
      );
    }

    return { data, labels };
  };

  const generateLastYearData = (trxList) => {
    const data = [];
    const labels = [];

    console.log(
      "🔍 Generating chart from transactions (1 year):",
      trxList.length
    );

    // Get last 12 months starting from January of current year
    const today = new Date();
    const currentYear = today.getFullYear();

    // Start from January
    for (let i = 0; i < 12; i++) {
      const monthStart = new Date(currentYear, i, 1);
      monthStart.setHours(0, 0, 0, 0);
      const monthEnd = new Date(currentYear, i + 1, 0);
      monthEnd.setHours(23, 59, 59, 999);

      labels.push(monthStart.toLocaleDateString("id-ID", { month: "short" }));

      const monthTransactions = trxList.filter((t) => {
        const trxDate = new Date(t.date || t.created_at);
        return trxDate >= monthStart && trxDate <= monthEnd;
      });

      const monthIncome = monthTransactions
        .filter(
          (t) =>
            t.type === "income" ||
            t.type === "pemasukan" ||
            t.type === "pemasukkan"
        )
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

      data.push(monthIncome / 1000);
    }
    return { data, labels };
  };

  // Update chart when period changes
  const handlePeriodChange = (period) => {
    setChartPeriod(period);
    const { data: newData, labels: newLabels } = generateChartData(
      transactions,
      period
    );
    setChartData(newData);
    setChartLabels(newLabels);
  };

  const getChartTitle = () => {
    const titles = {
      "7days": "Ringkasan Pemasukan (7 Hari Terakhir)",
      "1month": "Ringkasan Pemasukan (1 Bulan Terakhir)",
      "3months": "Ringkasan Pemasukan (3 Bulan Terakhir)",
      "6months": "Ringkasan Pemasukan (6 Bulan Terakhir)",
      "1year": "Ringkasan Pemasukan (1 Tahun Terakhir)",
    };
    return titles[chartPeriod] || "Ringkasan Pemasukan";
  };
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "4px solid #f1f5f9",
            borderTopColor: "#3b82f6",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
          Memuat dashboard...
        </p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <div
        style={{ maxWidth: "1400px", margin: "0 auto", padding: "32px 24px" }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            marginBottom: "32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: "#0f172a",
                margin: 0,
                marginBottom: "4px",
              }}
            >
              Ringkasan Dashboard
            </h1>
            <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
              Selamat datang, {currentUser?.name || "User"} 👋
            </p>
          </div>
          <button
            onClick={() => loadDashboardData()}
            style={{
              padding: "10px 16px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: "#ffffff",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </motion.div>

        {/* Main Stats Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "20px",
            marginBottom: "24px",
          }}
        >
          <ModernStatCard
            title="Saldo Total"
            value={formatCurrency(stats.balance)}
            subtitle={`${stats.transactionCount} transaksi`}
            icon={Wallet}
            variant="primary"
            trend={trends.balanceTrend >= 0 ? "up" : "down"}
            trendValue={`${Math.abs(trends.balanceTrend).toFixed(1)}%`}
            delay={0}
          />

          <ModernStatCard
            title="Total Pemasukan"
            value={formatCurrency(stats.totalIncome)}
            subtitle="Bulan ini"
            icon={TrendingUp}
            variant="success"
            trend={trends.incomeTrend >= 0 ? "up" : "down"}
            trendValue={`${Math.abs(trends.incomeTrend).toFixed(1)}%`}
            delay={0.05}
          />

          <ModernStatCard
            title="Total Pengeluaran"
            value={formatCurrency(stats.totalExpense)}
            subtitle="Bulan ini"
            icon={TrendingDown}
            variant="danger"
            trend={trends.expenseTrend >= 0 ? "up" : "down"}
            trendValue={`${Math.abs(trends.expenseTrend).toFixed(1)}%`}
            delay={0.1}
          />

          <ModernStatCard
            title="Transaksi"
            value={stats.transactionCount}
            subtitle="Transaksi bulan ini"
            icon={Activity}
            variant="warning"
            trend={trends.transactionTrend >= 0 ? "up" : "down"}
            trendValue={`${Math.abs(trends.transactionTrend).toFixed(1)}%`}
            delay={0.15}
          />
        </div>

        {/* Quick Stats Row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isAdmin
              ? "repeat(auto-fit, minmax(180px, 1fr))"
              : "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          {isAdmin && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "16px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  background: "#eff6ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Users size={20} color="#3b82f6" strokeWidth={2} />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                  Pengguna
                </p>
                <h4
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#0f172a",
                  }}
                >
                  {stats.usersCount}
                </h4>
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              padding: "16px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                background: "#f0fdf4",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Store size={20} color="#22c55e" strokeWidth={2} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                Outlet
              </p>
              <h4
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#0f172a",
                }}
              >
                {stats.outletsCount}
              </h4>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              padding: "16px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                background: "#fef3c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Tags size={20} color="#f59e0b" strokeWidth={2} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                Kategori
              </p>
              <h4
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#0f172a",
                }}
              >
                {stats.categoriesCount}
              </h4>
            </div>
          </motion.div>
        </div>

        {/* Charts and Activity */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "20px",
          }}
        >
          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35 }}
            style={{ gridColumn: "1 / -1" }}
          >
            <ActivityList transactions={transactions} />
          </motion.div>

          {/* Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            style={{ gridColumn: "1 / -1" }}
          >
            {/* Period selector buttons */}
            <div style={{ marginBottom: "16px", display: "flex", gap: "8px" }}>
              {[
                { label: "7 Hari", value: "7days" },
                { label: "1 Bulan", value: "1month" },
                { label: "3 Bulan", value: "3months" },
                { label: "6 Bulan", value: "6months" },
                { label: "1 Tahun", value: "1year" },
              ].map((period) => (
                <button
                  key={period.value}
                  onClick={() => handlePeriodChange(period.value)}
                  style={{
                    padding: "8px 16px",
                    border:
                      chartPeriod === period.value
                        ? "2px solid #3b82f6"
                        : "1px solid #e2e8f0",
                    background:
                      chartPeriod === period.value ? "#eff6ff" : "#ffffff",
                    color: chartPeriod === period.value ? "#3b82f6" : "#64748b",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: chartPeriod === period.value ? "600" : "500",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {period.label}
                </button>
              ))}
            </div>

            <BarChart
              data={chartData}
              labels={chartLabels}
              title={getChartTitle()}
              height={240}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
